import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Flex, Icon, Spinner, Text, useColorModeValue } from "@chakra-ui/react";
import { MdGavel } from "react-icons/md";
import Card from "horizon/components/card/Card";
import { fetchConfiscateRequests } from "./confiscationApi";
import { ApproveConfiscateDialog, RejectConfiscateDialog } from "./ConfiscationDialogs";
import ConfiscationTable from "./ConfiscationTable";

/**
 * Loads pending confiscation requests into state, surfacing a toast on error.
 * @param {Function} setItems - Setter for the request list
 * @param {Function} setLoading - Setter for the loading flag
 * @returns {Promise<void>}
 */
async function loadRequests(setItems, setLoading) {
  setLoading(true);
  try {
    const data = await fetchConfiscateRequests("pending_approval");
    setItems(data?.items || []);
  } catch (error) {
    toast.error(typeof error?.response?.data === "string" ? error.response.data : "Chargement des demandes impossible");
    setItems([]);
  } finally {
    setLoading(false);
  }
}

/**
 * Renders the page header card with title and a 4-eyes reminder.
 * @param {Object} props - count (number of pending requests)
 * @returns {React.ReactElement}
 */
function PageHeader({ count }) {
  const titleColor = useColorModeValue("secondaryGray.900", "white");
  const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  return (
    <Card>
      <Flex align="center" gap="14px">
        <Flex align="center" justify="center" w="44px" h="44px" borderRadius="12px" bg="red.50" _dark={{ bg: "whiteAlpha.100" }}>
          <Icon as={MdGavel} w="22px" h="22px" color="red.500" />
        </Flex>
        <Flex direction="column" minW="0">
          <Text fontSize="lg" fontWeight="700" color={titleColor}>Demandes de confiscation ({count})</Text>
          <Text fontSize="sm" color={subColor}>
            Principe des 4 yeux : l'approbateur doit être un super admin différent du demandeur. L'approbation exige une vérification 2FA.
          </Text>
        </Flex>
      </Flex>
    </Card>
  );
}

/**
 * Super Admin page listing pending wallet/group confiscation requests with
 * approve (2FA + 4-eyes) and reject actions.
 * @returns {React.ReactElement}
 */
function ConfiscationRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveReq, setApproveReq] = useState(null);
  const [rejectReq, setRejectReq] = useState(null);

  useEffect(() => { loadRequests(setItems, setLoading); }, []);
  const refresh = () => loadRequests(setItems, setLoading);

  return (
    <Flex direction="column" gap="20px">
      <PageHeader count={items.length} />
      {loading
        ? <Flex justify="center" py="40px"><Spinner color="brand.500" /></Flex>
        : <ConfiscationTable items={items} onApprove={setApproveReq} onReject={setRejectReq} />}
      <ApproveConfiscateDialog open={!!approveReq} request={approveReq}
        onClose={() => setApproveReq(null)} onDone={refresh} />
      <RejectConfiscateDialog open={!!rejectReq} request={rejectReq}
        onClose={() => setRejectReq(null)} onDone={refresh} />
    </Flex>
  );
}

export default ConfiscationRequests;
